import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import reorderQuantityService, {
  ReorderQuantity,
  ReorderQuantityFilters,
  Warehouse,
  Category
} from '../../services/reorderQuantityService';
import { useReportPermissions } from '../../hooks/useReportPermissions';
import { useReorderQuantitiesReportPermissions } from '../../hooks/useReorderQuantitiesReportPermissions';

const ReorderQuantitiesReportPage: React.FC = () => {
  // Permissions
  const { hasReportPagePermission, hasReportExportPermission, hasReportPrintPermission } = useReportPermissions();
  const {
    hasAccessPermission,
    hasExportPermission,
    hasPrintPermission,
    hasViewPermission
  } = useReorderQuantitiesReportPermissions();

  // State management
  const [reorderQuantities, setReorderQuantities] = useState<ReorderQuantity[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters and pagination
  const [filters, setFilters] = useState<ReorderQuantityFilters>({
    page: 1,
    limit: 50,
    search: '',
    category: '',
    warehouse: '',
    sortBy: 'productName',
    sortOrder: 'asc'
  });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // UI state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Statistics
  const [statistics, setStatistics] = useState({
    totalProducts: 0,
    totalVariants: 0,
    lowStockCount: 0,
    reorderNeededCount: 0,
    goodStockCount: 0
  });

  // Load data
  const loadReorderQuantities = useCallback(async () => {
    try {
      setLoading(true);
      // إرسال الفلاتر بدون التصنيفات المتعددة للـ API
      const apiFilters = { ...filters };
      delete apiFilters.category; // إزالة فلتر التصنيف من API
      
      const response = await reorderQuantityService.getReorderQuantities(apiFilters);
      
      let filteredData = response.data;
      
      // تطبيق فلترة التصنيفات المتعددة محلياً
      if (selectedCategories.length > 0) {
        filteredData = filteredData.filter(rq => {
          if (!rq.categories || rq.categories.length === 0) return false;
          return rq.categories.some(cat => selectedCategories.includes(cat._id));
        });
      }
      
      setReorderQuantities(filteredData);
      setTotalPages(response.pagination.pages);
      // استخدام العدد الإجمالي من الـ API وليس عدد البيانات المحملة في الصفحة الحالية
      setTotalItems(response.pagination.total);
      calculateStatistics(filteredData);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [filters, selectedCategories]);

  const loadWarehouses = async () => {
    try {
      const response = await reorderQuantityService.getWarehouses();
      setWarehouses(response.data);
    } catch (error: any) {
      console.error('Error loading warehouses:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await reorderQuantityService.getCategories();
      setCategories(response.data);
    } catch (error: any) {
      console.error('Error loading categories:', error);
      // تحميل البيانات أولاً ثم استخراج التصنيفات
      setTimeout(() => {
        extractCategoriesFromData();
      }, 1000);
    }
  };

  // استخراج التصنيفات من البيانات الموجودة
  const extractCategoriesFromData = () => {
    if (!reorderQuantities || reorderQuantities.length === 0) {
      return;
    }
    
    const uniqueCategories = new Map<string, Category>();
    
    reorderQuantities.forEach(rq => {
      if (rq.categories && rq.categories.length > 0) {
        rq.categories.forEach(category => {
          if (!uniqueCategories.has(category._id)) {
            uniqueCategories.set(category._id, category);
          }
        });
      }
    });
    
    setCategories(Array.from(uniqueCategories.values()));
  };

  useEffect(() => {
    loadReorderQuantities();
  }, [loadReorderQuantities]);

  useEffect(() => {
    loadWarehouses();
    loadCategories();
  }, []);

  // استخراج التصنيفات عند تحديث البيانات
  useEffect(() => {
    if (reorderQuantities && reorderQuantities.length > 0 && (!categories || categories.length === 0)) {
      setTimeout(() => {
        extractCategoriesFromData();
      }, 500);
    }
  }, [reorderQuantities]);

  // Calculate statistics
  const calculateStatistics = (data: ReorderQuantity[]) => {
    const groupedByProduct = data.reduce((acc, rq) => {
      // التحقق من وجود المنتج قبل الوصول إلى خصائصه
      if (!rq.product || !rq.product._id) {
        return acc;
      }
      
      const productId = rq.product._id;
      if (!acc[productId]) {
        acc[productId] = {
          product: rq.product,
          variants: []
        };
      }
      acc[productId].variants.push(rq);
      return acc;
    }, {} as Record<string, { product: any; variants: ReorderQuantity[] }>);

    let lowStockCount = 0;
    let reorderNeededCount = 0;
    let goodStockCount = 0;

    data.forEach(rq => {
      let hasLowStock = false;
      let hasReorderNeeded = false;
      let hasGoodStock = false;

      rq.warehouseReorderQuantities?.forEach(wrq => {
        // الحصول على الكمية الحالية من API
        const rawCurrentStock = wrq.currentStock || 0;
        // معالجة الكميات السالبة: نعتبرها صفر للحساب
        const currentStock = rawCurrentStock < 0 ? 0 : rawCurrentStock;
        const reorderQuantity = wrq.reorderQuantity || 0;
        // إذا كانت الكمية الحالية أكبر من أو تساوي كمية إعادة الطلب، الفرق = 0
        const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;

        if (difference < -5) hasLowStock = true;
        else if (difference < 0) hasReorderNeeded = true;
        else hasGoodStock = true;
      });

      if (hasLowStock) lowStockCount++;
      else if (hasReorderNeeded) reorderNeededCount++;
      else if (hasGoodStock) goodStockCount++;
    });

    setStatistics({
      totalProducts: Object.keys(groupedByProduct).length,
      totalVariants: data ? data.length : 0,
      lowStockCount,
      reorderNeededCount,
      goodStockCount
    });
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof ReorderQuantityFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  // Handle search
  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Export to Excel
  const handleExportToExcel = async () => {
    // التحقق من صلاحية التصدير
    if (!hasExportPermission()) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية لتصدير هذا التقرير',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Get all data by making multiple requests if needed
      let allData: ReorderQuantity[] = [];
      let currentPage = 1;
      let hasMoreData = true;
      
      while (hasMoreData) {
        const exportFilters = { ...filters, page: currentPage, limit: 100 };
        delete exportFilters.category; // إزالة فلتر التصنيف من API
        
        const response = await reorderQuantityService.getReorderQuantities(exportFilters);
        
        allData = [...allData, ...response.data];
        
        // Check if there are more pages
        hasMoreData = currentPage < response.pagination.pages;
        currentPage++;
        
        // Safety check to avoid infinite loop
        if (currentPage > 50) break;
      }

      // تطبيق فلترة التصنيفات المتعددة على بيانات التصدير
      if (selectedCategories.length > 0) {
        allData = allData.filter(rq => {
          if (!rq.categories || rq.categories.length === 0) return false;
          return rq.categories.some(cat => selectedCategories.includes(cat._id));
        });
      }

      // Group data by product (same as display logic)
      const groupedByProduct = allData.reduce((acc, rq) => {
        // التحقق من وجود المنتج قبل الوصول إلى خصائصه
        if (!rq.product || !rq.product._id) {
          return acc;
        }
        
        const productId = rq.product._id;
        if (!acc[productId]) {
          acc[productId] = {
            product: rq.product,
            productName: rq.productName,
            variants: []
          };
        }
        acc[productId].variants.push(rq);
        return acc;
      }, {} as Record<string, { product: any; productName: string; variants: ReorderQuantity[] }>);

      // Sort variants within each product group by variantSerialNumber
      Object.values(groupedByProduct).forEach(group => {
        group.variants.sort((a, b) => a.variantSerialNumber - b.variantSerialNumber);
      });

      // Prepare Excel data
      const excelData: any[] = [];
      
      Object.values(groupedByProduct).forEach(group => {
        // Add product header row
        excelData.push({
          'اسم المنتج': `📦 ${group.productName}`,
          'الرقم التسلسلي': '',
          'التصنيف': '',
          'المتغير': '',
          'الباركود': '',
          'الوحدة': '',
          ...warehouses.reduce((acc, warehouse) => {
            acc[`${warehouse.name} - الكمية الحالية`] = '';
            acc[`${warehouse.name} - كمية إعادة الطلب`] = '';
            acc[`${warehouse.name} - الفرق`] = '';
            return acc;
          }, {} as Record<string, string>),
          'مجموع الفروق': '',
          'الحالة العامة': ''
        });

        // Add variant rows
        group.variants.forEach(rq => {
          const variantAttributes = rq.variantAttributes && typeof rq.variantAttributes === 'object'
            ? Object.entries(rq.variantAttributes).map(([key, value]) => `${key}: ${value}`).join(', ')
            : '';

          const row: any = {
            'اسم المنتج': '',
            'الرقم التسلسلي': rq.variantSerialNumber,
            'التصنيف': (rq.categories && rq.categories.length > 0)
              ? rq.categories.map(cat => cat.name).join(', ')
              : 'غير محدد',
            'المتغير': variantAttributes,
            'الباركود': rq.barcode || '',
            'الوحدة': rq.unit || ''
          };

          // Add warehouse data and calculate total difference
          let totalDifference = 0;
          warehouses.forEach(warehouse => {
            const warehouseQuantity = rq.warehouseReorderQuantities?.find(
              wrq => wrq.warehouse._id === warehouse._id
            );
            const reorderQuantity = warehouseQuantity?.reorderQuantity || 0;
            const rawCurrentStock = warehouseQuantity?.currentStock || 0;
            // معالجة الكميات السالبة: نعرضها كما هي لكن نحسب الفرق بصفر
            const currentStock = rawCurrentStock;
            const currentStockForCalc = rawCurrentStock < 0 ? 0 : rawCurrentStock;
            // إذا كانت الكمية الحالية أكبر من أو تساوي كمية إعادة الطلب، الفرق = 0
            const difference = currentStockForCalc >= reorderQuantity ? 0 : currentStockForCalc - reorderQuantity;
            totalDifference += difference;

            row[`${warehouse.name} - الكمية الحالية`] = currentStock;
            row[`${warehouse.name} - كمية إعادة الطلب`] = reorderQuantity;
            row[`${warehouse.name} - الفرق`] = difference;
          });

          // Add total difference
          row['مجموع الفروق'] = totalDifference;

          // Determine overall status
          let overallStatus = 'مخزون جيد';
          const hasLowStock = warehouses.some(warehouse => {
            const warehouseQuantity = rq.warehouseReorderQuantities?.find(
              wrq => wrq.warehouse._id === warehouse._id
            );
            const reorderQuantity = warehouseQuantity?.reorderQuantity || 0;
            const rawCurrentStock = warehouseQuantity?.currentStock || 0;
            const currentStock = rawCurrentStock < 0 ? 0 : rawCurrentStock;
            const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;
            return difference < -5;
          });
          
          const hasReorderNeeded = warehouses.some(warehouse => {
            const warehouseQuantity = rq.warehouseReorderQuantities?.find(
              wrq => wrq.warehouse._id === warehouse._id
            );
            const reorderQuantity = warehouseQuantity?.reorderQuantity || 0;
            const rawCurrentStock = warehouseQuantity?.currentStock || 0;
            const currentStock = rawCurrentStock < 0 ? 0 : rawCurrentStock;
            const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;
            return difference < 0 && difference >= -5;
          });

          if (hasLowStock) overallStatus = 'مخزون منخفض';
          else if (hasReorderNeeded) overallStatus = 'يحتاج إعادة طلب';

          row['الحالة العامة'] = overallStatus;
          excelData.push(row);
        });

        // Add empty row between products
        excelData.push({});
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 30 }, // اسم المنتج
        { wch: 15 }, // الرقم التسلسلي
        { wch: 25 }, // المتغير
        { wch: 20 }, // الباركود
        { wch: 10 }, // الوحدة
        ...warehouses.flatMap(() => [
          { wch: 15 }, // الكمية الحالية
          { wch: 15 }, // كمية إعادة الطلب
          { wch: 10 }  // الفرق
        ]),
        { wch: 15 }  // الحالة العامة
      ];
      worksheet['!cols'] = columnWidths;

      // تطبيق التنسيق العربي RTL للورقة
      worksheet['!dir'] = 'rtl';
      
      // إعداد خصائص الورقة للعربية
      if (!worksheet['!worksheetViews']) {
        worksheet['!worksheetViews'] = [{}];
      }
      worksheet['!worksheetViews'][0].rightToLeft = true;

      // محاذاة الخلايا للعربية مع RTL
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (worksheet[cellAddress]) {
            if (!worksheet[cellAddress].s) {
              worksheet[cellAddress].s = {};
            }
            worksheet[cellAddress].s.alignment = {
              horizontal: 'right',
              vertical: 'center',
              readingOrder: 2, // RTL reading order
              textRotation: 0
            };
          }
        }
      }

      // إعداد خصائص الكتاب للعربية
      if (!workbook.Workbook) {
        workbook.Workbook = {};
      }
      if (!workbook.Workbook.Views) {
        workbook.Workbook.Views = [{}];
      }
      workbook.Workbook.Views[0].RTL = true;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'تقرير كميات إعادة الطلب');

      // Generate filename with current date
      const currentDate = new Date().toLocaleDateString('ar-SA').replace(/\//g, '-');
      const filename = `تقرير_كميات_اعادة_الطلب_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      setSnackbar({
        open: true,
        message: 'تم تصدير التقرير بنجاح',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'خطأ في تصدير التقرير',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Print report
  const handlePrintReport = () => {
    // التحقق من صلاحية الطباعة
    if (!hasPrintPermission()) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية لطباعة هذا التقرير',
        severity: 'error'
      });
      return;
    }

    window.print();
  };

  // Format variant attributes
  const formatVariantAttributes = (attributes: Record<string, string>) => {
    if (!attributes || typeof attributes !== 'object') {
      return null;
    }
    return Object.entries(attributes).map(([key, value]) => (
      <Chip
        key={key}
        label={`${key}: ${value}`}
        size="small"
        variant="outlined"
        sx={{ margin: 0.25 }}
      />
    ));
  };

  // Check permissions
  if (!hasAccessPermission()) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          ليس لديك صلاحية للوصول إلى هذا التقرير
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssessmentIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '2rem' }} />
          <Typography variant="h4" component="h1" gutterBottom>
            تقرير كميات إعادة الطلب
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          تقرير شامل لكميات إعادة الطلب لجميع المنتجات والمتغيرات في المستودعات
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {statistics.lowStockCount}
              </Typography>
              <Typography variant="body2">
                مخزون منخفض
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {statistics.reorderNeededCount}
              </Typography>
              <Typography variant="body2">
                يحتاج إعادة طلب
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {statistics.goodStockCount}
              </Typography>
              <Typography variant="body2">
                مخزون جيد
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {statistics.totalProducts}
              </Typography>
              <Typography variant="body2">
                إجمالي المنتجات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {statistics.totalVariants}
              </Typography>
              <Typography variant="body2">
                إجمالي المتغيرات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="البحث في المنتجات"
              placeholder="اسم المنتج، الباركود، أو SKU"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>التصنيفات</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={(e) => {
                  const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setSelectedCategories(value);
                  // لا نرسل التصنيفات للـ API، فقط فلترة محلية
                }}
                label="التصنيفات"
                renderValue={(selected) => {
                  if (selected.length === 0) return 'جميع التصنيفات';
                  if (selected.length === 1) {
                    const category = categories.find(cat => cat._id === selected[0]);
                    return category?.name || 'تصنيف محدد';
                  }
                  return `${selected.length} تصنيفات محددة`;
                }}
              >
                <MenuItem value="">
                  <em>جميع التصنيفات</em>
                </MenuItem>
                {categories && categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category._id)}
                        onChange={() => {}}
                        style={{ marginLeft: 8 }}
                      />
                      {category.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>المستودع</InputLabel>
              <Select
                value={filters.warehouse || ''}
                onChange={(e) => handleFilterChange('warehouse', e.target.value)}
                label="المستودع"
              >
                <MenuItem value="">جميع المستودعات</MenuItem>
                {warehouses && warehouses.map((warehouse) => (
                  <MenuItem key={warehouse._id} value={warehouse._id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleSearch}
            >
              بحث
            </Button>
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={() => {
                setSelectedCategories([]);
                setFilters({
                  page: 1,
                  limit: 50,
                  search: '',
                  category: '',
                  warehouse: '',
                  sortBy: 'productName',
                  sortOrder: 'asc'
                });
              }}
            >
              مسح
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={hasExportPermission() ? "تصدير Excel" : "ليس لديك صلاحية للتصدير"}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleExportToExcel}
                    disabled={loading || !hasExportPermission()}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={hasPrintPermission() ? "طباعة" : "ليس لديك صلاحية للطباعة"}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handlePrintReport}
                    disabled={!hasPrintPermission()}
                  >
                    <PrintIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            تقرير كميات إعادة الطلب
          </Typography>
          <Typography variant="body2" color="text.secondary">
            إجمالي المنتجات: {statistics.totalProducts} | إجمالي المتغيرات: {statistics.totalVariants}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>الرقم التسلسلي</TableCell>
                <TableCell>اسم المنتج</TableCell>
                <TableCell>التصنيف</TableCell>
                <TableCell>المتغير</TableCell>
                <TableCell>الباركود</TableCell>
                <TableCell>الوحدة</TableCell>
                {warehouses && warehouses.map((warehouse) => (
                  <TableCell key={warehouse._id} align="center" sx={{ minWidth: 120 }}>
                    {warehouse.name}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      (الحالي / إعادة الطلب)
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="center">مجموع الفروق</TableCell>
                <TableCell align="center">الحالة العامة</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reorderQuantities && (() => {
                // Group reorder quantities by product (same logic as ReorderQuantitiesPage)
                const groupedByProduct = reorderQuantities.reduce((acc, rq) => {
                  // التحقق من وجود المنتج قبل الوصول إلى خصائصه
                  if (!rq.product || !rq.product._id) {
                    console.warn('تحذير: منتج بدون معرف:', rq);
                    return acc;
                  }
                  
                  const productId = rq.product._id;
                  if (!acc[productId]) {
                    acc[productId] = {
                      product: rq.product,
                      productName: rq.productName,
                      variants: []
                    };
                  }
                  acc[productId].variants.push(rq);
                  return acc;
                }, {} as Record<string, { product: any; productName: string; variants: typeof reorderQuantities }>);

                // Sort variants within each product group by variantSerialNumber
                Object.values(groupedByProduct).forEach(group => {
                  group.variants.sort((a, b) => a.variantSerialNumber - b.variantSerialNumber);
                });

                return Object.values(groupedByProduct).map((group) => (
                  <React.Fragment key={group.product._id}>
                    {/* Product header row */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell colSpan={6 + ((warehouses && warehouses.length) || 0) + 2} sx={{ fontWeight: 'bold', fontSize: '1.1em', py: 1 }}>
                        📦 {group.productName}
                      </TableCell>
                    </TableRow>
                    {/* Variant rows */}
                    {group.variants.map((rq) => {
                      // Calculate overall status for this variant
                      let overallStatus = 'good';
                      let hasLowStock = false;
                      let hasReorderNeeded = false;

                      rq.warehouseReorderQuantities?.forEach(wrq => {
                        const rawCurrentStock = wrq.currentStock || 0;
                        const currentStock = rawCurrentStock < 0 ? 0 : rawCurrentStock;
                        const reorderQuantity = wrq.reorderQuantity || 0;
                        const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;

                        if (difference < -5) hasLowStock = true;
                        else if (difference < 0) hasReorderNeeded = true;
                      });

                      if (hasLowStock) overallStatus = 'low';
                      else if (hasReorderNeeded) overallStatus = 'reorder';

                      const statusText = overallStatus === 'low' ? 'مخزون منخفض' :
                                        overallStatus === 'reorder' ? 'يحتاج إعادة طلب' : 'مخزون جيد';
                      const statusColor = overallStatus === 'low' ? 'error' :
                                         overallStatus === 'reorder' ? 'warning' : 'success';

                      // حساب مجموع الفروق لجميع المستودعات
                      let totalDifference = 0;
                      rq.warehouseReorderQuantities?.forEach(wrq => {
                        const rawCurrentStock = wrq.currentStock || 0;
                        const currentStock = rawCurrentStock < 0 ? 0 : rawCurrentStock;
                        const reorderQuantity = wrq.reorderQuantity || 0;
                        const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;
                        totalDifference += difference;
                      });

                      return (
                        <TableRow key={rq._id} hover>
                          <TableCell sx={{ paddingLeft: 4 }}>
                            <Chip
                              label={rq.variantSerialNumber}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              متغير
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(rq.categories && rq.categories.length > 0) ? (
                                rq.categories.map(cat => (
                                  <Chip
                                    key={cat._id}
                                    label={cat.name}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  غير محدد
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {formatVariantAttributes(rq.variantAttributes)}
                            </Box>
                          </TableCell>
                          <TableCell>{rq.barcode}</TableCell>
                          <TableCell>{rq.unit}</TableCell>
                          {warehouses && warehouses.map((warehouse) => {
                            const warehouseQuantity = rq.warehouseReorderQuantities && rq.warehouseReorderQuantities.find(
                              wrq => wrq.warehouse._id === warehouse._id
                            );
                            const reorderQuantity = warehouseQuantity?.reorderQuantity || 0;
                            const rawCurrentStock = warehouseQuantity?.currentStock || 0;
                            const isNegativeStock = rawCurrentStock < 0;
                            const currentStock = isNegativeStock ? 0 : rawCurrentStock;
                            // إذا كانت الكمية الحالية أكبر من أو تساوي كمية إعادة الطلب، الفرق = 0
                            const difference = currentStock >= reorderQuantity ? 0 : currentStock - reorderQuantity;

                            return (
                              <TableCell key={warehouse._id} align="center">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {isNegativeStock ? (
                                      <span style={{ color: '#d32f2f', textDecoration: 'line-through' }}>
                                        {rawCurrentStock}
                                      </span>
                                    ) : (
                                      rawCurrentStock
                                    )} / {reorderQuantity}
                                  </Typography>
                                  {isNegativeStock && (
                                    <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.65rem' }}>
                                      ⚠️ كمية سالبة
                                    </Typography>
                                  )}
                                  <Chip
                                    label={difference === 0 ? '0' : difference}
                                    size="small"
                                    color={difference < -5 ? 'error' : difference < 0 ? 'warning' : 'success'}
                                    variant="outlined"
                                  />
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell align="center">
                            <Chip
                              label={totalDifference > 0 ? `+${totalDifference}` : totalDifference}
                              size="medium"
                              color={totalDifference < -10 ? 'error' : totalDifference < 0 ? 'warning' : 'success'}
                              sx={{ fontWeight: 'bold', minWidth: 60 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={statusText}
                              color={statusColor as any}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ));
              })()}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalItems}
          page={(filters.page || 1) - 1}
          onPageChange={(_, newPage) => handleFilterChange('page', newPage + 1)}
          rowsPerPage={filters.limit || 50}
          onRowsPerPageChange={(e) => {
            const value = e.target.value;
            // إذا اختار "الكل"، نستخدم -1 كقيمة خاصة
            handleFilterChange('limit', value === '-1' ? -1 : parseInt(value));
          }}
          rowsPerPageOptions={[
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
            { label: '200', value: 200 },
            { label: 'الكل', value: -1 }
          ]}
          labelRowsPerPage="عدد الصفوف في الصفحة:"
          labelDisplayedRows={({ from, to, count }) => {
            // إذا كان limit = -1 (الكل)، نعرض "1-{count} من {count}"
            if (filters.limit === -1) {
              return `1-${count} من ${count}`;
            }
            return `${from}-${to} من ${count}`;
          }}
        />
      </TableContainer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReorderQuantitiesReportPage;